import { API_BASE_URL } from "@/constants/api";
import { ATTRIBUTE_VALUE_TYPES, type AttributeValueType } from "@/lib/categories/attribute-definition";
import type { ExportFormat } from "@/lib/export-format";
import type { LanguageMap, OptionalLanguageMap } from "@/services/category.service";

export type CatalogueTemplateStatus = "draft" | "published" | "archived";

export interface CatalogueTemplateBase {
  id: string;
  clientId?: string;
  name: string;
  version: string;
  description?: string | null;
  status: CatalogueTemplateStatus;
  isDefault: boolean;
  languages: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

export type CategoryTemplate = CatalogueTemplateBase;
export type PublishedCategoryTemplate = CategoryTemplate & { status: "published" };

export interface CategoryTemplateCategory {
  id: string;
  templateId: string;
  parentId: string | null;
  key: string;
  name: LanguageMap;
  slug?: string | null;
  category_slug: OptionalLanguageMap | null;
  description?: string | null;
  sortOrder: number;
}

export interface CategoryTemplateAttribute {
  id: string;
  templateId: string;
  categoryId: string | null;
  key: string;
  label: LanguageMap;
  slug?: OptionalLanguageMap | null;
  valueType: AttributeValueType;
  required: boolean;
  configuration: Record<string, unknown>;
  sortOrder: number;
}

export interface CategoryTemplateDetail extends CatalogueTemplateBase {
  categories: CategoryTemplateCategory[];
  attributes: CategoryTemplateAttribute[];
}

export const CATEGORY_TEMPLATE_ATTRIBUTE_VALUE_TYPES = ATTRIBUTE_VALUE_TYPES;
export type CategoryTemplateAttributeValueType = AttributeValueType;

export interface TemplateListQuery {
  status?: CatalogueTemplateStatus;
}

export interface TemplateCreateInput {
  name: string;
  version: string;
  languages: string[];
  description?: string | null;
  is_default?: boolean;
}

export interface TemplateUpdateInput {
  name?: string;
  description?: string | null;
  is_default?: boolean;
}

export interface TemplateCloneInput {
  name: string;
  version: string;
  description?: string | null;
  is_default?: boolean;
}

export interface TemplatePublishInput {
  is_default?: boolean;
}

export interface TemplateArchiveInput {
  reason?: string;
}

export interface CategoryTemplateCategoryInput {
  category_slug?: OptionalLanguageMap | null;
  parent_id?: string | null;
  key: string;
  name: LanguageMap;
  description?: string | null;
  sort_order?: number;
}

export type CategoryTemplateCategoryUpdate = Partial<CategoryTemplateCategoryInput>;

export interface CategoryTemplateAttributeInput {
  category_id?: string | null;
  key: string;
  label: LanguageMap;
  slug?: OptionalLanguageMap;
  value_type?: CategoryTemplateAttributeValueType;
  required?: boolean;
  configuration?: Record<string, unknown>;
  sort_order?: number;
}

export type CategoryTemplateAttributeUpdate = Partial<CategoryTemplateAttributeInput>;
export type TemplateImportSource = Record<string, unknown>;

export interface TemplateImportPreview {
  valid: boolean;
  errors: Array<string | { path?: string; message?: string }>;
  summary: Record<string, unknown> | null;
}

export type TemplateImportTarget =
  | {
      mode: "create";
      name: string;
      version: string;
      description?: string | null;
      is_default?: boolean;
    }
  | { mode: "replace"; template_id: string };

export interface TemplateExportDownload {
  blob: Blob;
  filename: string;
}

type Wire = Record<string, any>;
const BASE = `${API_BASE_URL}/api/v1/catalogue-templates/category-attributes`;

function languagesFromWire(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return [
    ...new Set(
      value.flatMap((language) => {
        if (typeof language !== "string") return [];
        const normalized = language.trim();
        return normalized ? [normalized] : [];
      }),
    ),
  ];
}

function attachmentFilename(response: Response, fallback: string): string {
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) return decodeURIComponent(encoded.replace(/^"|"$/g, ""));
  return disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? fallback;
}

function categoryFromWire(value: Wire): CategoryTemplateCategory {
  return {
    id: value.id,
    templateId: value.template_id,
    parentId: value.parent_id ?? null,
    key: value.key,
    name: value.name,
    slug: value.slug ?? null,
    category_slug: value.category_slug ?? null,
    description: value.description ?? null,
    sortOrder: Number(value.sort_order ?? 0),
  };
}

function attributeFromWire(value: Wire): CategoryTemplateAttribute {
  return {
    id: value.id,
    templateId: value.template_id,
    categoryId: value.category_id ?? null,
    key: value.key,
    label: value.label,
    slug: value.slug ?? null,
    valueType: value.value_type,
    required: Boolean(value.required),
    configuration: value.configuration,
    sortOrder: Number(value.sort_order ?? 0),
  };
}

function templateFromWire(value: Wire): CategoryTemplate {
  return {
    id: value.id,
    clientId: value.client_id,
    name: value.name,
    version: value.version,
    description: value.description ?? null,
    status: value.status,
    isDefault: Boolean(value.is_default),
    languages: languagesFromWire(value.languages),
    createdAt: value.created_at ? new Date(value.created_at) : undefined,
    updatedAt: value.updated_at ? new Date(value.updated_at) : undefined,
  };
}

function detailFromWire(value: Wire): CategoryTemplateDetail {
  return {
    ...templateFromWire(value),
    categories: (value.categories ?? []).map(categoryFromWire),
    attributes: (value.attributes ?? []).map(attributeFromWire),
  };
}

async function request(path = "", init: RequestInit = {}): Promise<any> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
      ...init.headers,
    },
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok || !payload || typeof payload !== "object" || Array.isArray(payload)) {
    const error = payload as Wire | null;
    throw new Error(
      typeof error?.error === "string"
        ? error.error
        : `Template request failed (${response.status})`,
    );
  }
  return (payload as Wire).data;
}

const send = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

async function listCategoryTemplates(query: TemplateListQuery = {}): Promise<CategoryTemplate[]> {
  const suffix = query.status ? `?status=${encodeURIComponent(query.status)}` : "";
  const data = await request(suffix);
  return (data as Wire[]).map(templateFromWire);
}

const isPublished = (template: CategoryTemplate): template is PublishedCategoryTemplate =>
  template.status === "published";

export const CatalogueTemplateService = {
  listCategoryTemplates,

  listPublishedCategoryTemplates: async (): Promise<PublishedCategoryTemplate[]> =>
    (await listCategoryTemplates({ status: "published" })).filter(isPublished),

  createCategoryTemplate: async (input: TemplateCreateInput) =>
    templateFromWire(await request("", send("POST", input))),

  getCategoryTemplate: async (templateId: string) =>
    detailFromWire(await request(`/${encodeURIComponent(templateId)}`)),

  updateCategoryTemplate: async (templateId: string, input: TemplateUpdateInput) =>
    templateFromWire(
      await request(`/${encodeURIComponent(templateId)}`, send("PATCH", input)),
    ),

  cloneCategoryTemplate: async (templateId: string, input: TemplateCloneInput) =>
    detailFromWire(
      await request(`/${encodeURIComponent(templateId)}/clone`, send("POST", input)),
    ),

  publishCategoryTemplate: async (templateId: string, input: TemplatePublishInput = {}) =>
    detailFromWire(
      await request(`/${encodeURIComponent(templateId)}/publish`, send("POST", input)),
    ),

  archiveCategoryTemplate: async (templateId: string, input: TemplateArchiveInput = {}) =>
    detailFromWire(
      await request(`/${encodeURIComponent(templateId)}/archive`, send("POST", input)),
    ),

  listCategoryTemplateCategories: async (templateId: string) =>
    ((await request(`/${encodeURIComponent(templateId)}/categories`)) as Wire[])
      .map(categoryFromWire),

  createCategoryTemplateCategory: async (
    templateId: string,
    input: CategoryTemplateCategoryInput,
  ) => categoryFromWire(
    await request(`/${encodeURIComponent(templateId)}/categories`, send("POST", input)),
  ),

  getCategoryTemplateCategory: async (templateId: string, categoryId: string) =>
    categoryFromWire(
      await request(
        `/${encodeURIComponent(templateId)}/categories/${encodeURIComponent(categoryId)}`,
      ),
    ),

  updateCategoryTemplateCategory: async (
    templateId: string,
    categoryId: string,
    input: CategoryTemplateCategoryUpdate,
  ) => categoryFromWire(
    await request(
      `/${encodeURIComponent(templateId)}/categories/${encodeURIComponent(categoryId)}`,
      send("PATCH", input),
    ),
  ),

  deleteCategoryTemplateCategory: (templateId: string, categoryId: string) =>
    request(
      `/${encodeURIComponent(templateId)}/categories/${encodeURIComponent(categoryId)}`,
      send("DELETE"),
    ),

  listCategoryTemplateAttributes: async (templateId: string) =>
    ((await request(`/${encodeURIComponent(templateId)}/attributes`)) as Wire[])
      .map(attributeFromWire),

  createCategoryTemplateAttribute: async (
    templateId: string,
    input: CategoryTemplateAttributeInput,
  ) => attributeFromWire(
    await request(`/${encodeURIComponent(templateId)}/attributes`, send("POST", input)),
  ),

  getCategoryTemplateAttribute: async (templateId: string, attributeId: string) =>
    attributeFromWire(
      await request(
        `/${encodeURIComponent(templateId)}/attributes/${encodeURIComponent(attributeId)}`,
      ),
    ),

  updateCategoryTemplateAttribute: async (
    templateId: string,
    attributeId: string,
    input: CategoryTemplateAttributeUpdate,
  ) => attributeFromWire(
    await request(
      `/${encodeURIComponent(templateId)}/attributes/${encodeURIComponent(attributeId)}`,
      send("PATCH", input),
    ),
  ),

  deleteCategoryTemplateAttribute: (templateId: string, attributeId: string) =>
    request(
      `/${encodeURIComponent(templateId)}/attributes/${encodeURIComponent(attributeId)}`,
      send("DELETE"),
    ),

  exportCategoryTemplate: async (templateId: string, format: ExportFormat = "json") => {
    const response = await fetch(
      `${BASE}/${encodeURIComponent(templateId)}/export?format=${encodeURIComponent(format)}`,
      { credentials: "include" },
    );
    if (!response.ok) throw new Error(`Template export failed (${response.status})`);
    return {
      blob: await response.blob(),
      filename: attachmentFilename(response, `categories-and-attributes.${format}`),
    };
  },

  previewCategoryTemplateImport: (source: TemplateImportSource) =>
    request("/import/preview", send("POST", { document: source })) as Promise<TemplateImportPreview>,

  importCategoryTemplate: (source: TemplateImportSource, target: TemplateImportTarget) =>
    request("/import", send("POST", { document: source, target })),

  applyCategoryTemplate: (catalogueId: string, publishedTemplateId: string) =>
    fetch(`${API_BASE_URL}/api/v1/catalogue/${encodeURIComponent(catalogueId)}/apply-category-template`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ templateId: publishedTemplateId }),
    }).then(async (response) => {
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error ?? "Template application failed");
      return payload.data;
    }),
};
